import type { As } from "@chakra-ui/react";
import { useTranslations } from "contexts/LocalizationContext";
import { Grid } from "modules/common/components/Grid";
import { useMemo, useState } from "react";
import { Pagination } from "modules/common/components";
import type { SortingState } from "@tanstack/react-table";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
} from "@tanstack/react-table";
import { Box, Flex, Heading, useBreakpointValue } from "@chakra-ui/react";
import { Table, Filter } from "../../common";
import type { AirportRankingTableProps } from "./AirportRankingTable.d";

const AirportRankingTable = ({ columns, rows, heading }: AirportRankingTableProps) => {
  const memoColumns = useMemo(() => columns, [columns]);
  const memoRows = useMemo(() => rows, [rows]);
  const translations = useTranslations();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState<string>("");

  const {
    nextPage,
    previousPage,
    getCanNextPage,
    getCanPreviousPage,
    getPageOptions,
    setPageIndex,
    getHeaderGroups,
    getPageCount,
    getState,
    getRowModel,
  } = useReactTable({
    columns: memoColumns,
    data: memoRows,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    debugTable: true,
  });
  const headingSize = useBreakpointValue({ base: "h2", lg: "h3" });

  return (
    <Box
      bg="greyscale.300"
      pt={14}
      pb={{ base: 0, lg: 14 }}
      borderBottom={{ base: "1px solid", lg: "unset" }}
      borderColor={{ base: "greyscale.300", lg: "unset" }}
    >
      <Grid mb={0} bg={{ base: "greyscale.100", lg: "transparent" }}>
        <Box
          gridColumn={{ base: "span 1", md: "span 12" }}
          bg="greyscale.100"
          borderRadius={{ base: 0, lg: "lg" }}
          px={{ base: 0, lg: 9 }}
          py={9}
        >
          <Flex
            flexDir={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
          >
            <Heading variant={headingSize} as={headingSize as As}>
              {translations(heading)}
            </Heading>
            <Filter
              filter={globalFilter}
              setFilter={setGlobalFilter}
              placeholder={translations("search_for_airport")}
            />
          </Flex>
          <Table rows={getRowModel().rows} headerGroups={getHeaderGroups()} />
          {getPageOptions().length ? (
            <Pagination
              previousPage={previousPage}
              canPreviousPage={getCanPreviousPage()}
              pages={getPageCount()}
              nextPage={nextPage}
              currentPage={getState().pagination.pageIndex + 1}
              canNextPage={getCanNextPage()}
              goToPage={(pageIndex) => setPageIndex(pageIndex - 1)}
            />
          ) : (
            <Box textAlign="center">-</Box>
          )}
        </Box>
      </Grid>
    </Box>
  );
};

export default AirportRankingTable;
